// IssueSummaryStrip — four read-only tiles above the issue register:
//   Open               = count of UNRESOLVED issues (open / in progress /
//                         awaiting response — i.e. not resolved or closed)
//   Awaiting response   = count of issues in status 'awaiting_response'
//   Overdue actions     = count of actions (across every issue's denormalised
//                         action register) whose derived state is 'overdue'
//   Resolved last 30d   = count of issues whose resolvedAt falls in the
//                         trailing 30 days
//
// Conditional tone (brand rule): the three problem tiles take a red value
// tone ONLY when their count > 0; otherwise they sit on the neutral palette
// so a calm contract doesn't carry an active-failure signal. The Resolved
// tile is always neutral — it's a positive count, not a problem. Action
// overdue-ness is derived on read via the shared deriveIssueActionState so
// the tile can never drift from the row/drawer action state.

import MetricCard from "../deliverable/MetricCard";
import { deriveIssueActionState } from "./deriveIssueDisplay";
import { getBrisbaneToday } from "../../../../utils/getBrisbaneToday";
import type { Issue } from "../../../../types";

export interface IssueSummaryStripProps {
  issues: Issue[];
  today?: Date;
}

const DAY_MS = 86_400_000;

function startOfUTCDay(d: Date): number {
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
}

/** YYYY-MM-DD UTC day string for the shared date-string derivations. */
function toIsoDay(d: Date): string {
  return new Date(startOfUTCDay(d)).toISOString().slice(0, 10);
}

function isoToUTCDay(iso: string): number | null {
  const parsed = new Date(`${iso.slice(0, 10)}T00:00:00Z`);
  if (Number.isNaN(parsed.getTime())) return null;
  return startOfUTCDay(parsed);
}

export default function IssueSummaryStrip({
  issues,
  today,
}: IssueSummaryStripProps) {
  // Brisbane calendar day (matching backend + parity fixtures), not the browser
  // wall-clock; tests inject a fixed `today` Date. todayMs derives from the same
  // anchor so the overdue-action and resolved-30d windows stay consistent.
  const todayIso = today ? toIsoDay(today) : getBrisbaneToday();
  const todayMs = isoToUTCDay(todayIso) ?? startOfUTCDay(new Date());

  // "Open" counts every UNRESOLVED issue (open / in progress / awaiting
  // response), not just the literal 'open' status. Counting only 'open' read
  // as misleading: an in-progress issue left the headline at 0 while the tab
  // badge and the old "active issue" sub-line both said 1.
  const unresolvedCount = issues.filter(
    (i) => i.status !== "resolved" && i.status !== "closed",
  ).length;
  const awaitingCount = issues.filter(
    (i) => i.status === "awaiting_response",
  ).length;

  // Open sub-line: the longest any unresolved issue has been open, in whole
  // days from raisedAt — a staleness signal for the live cohort.
  let oldestOpenDays = 0;
  for (const issue of issues) {
    if (issue.status === "resolved" || issue.status === "closed") continue;
    const raised = isoToUTCDay(issue.raisedAt);
    if (raised === null) continue;
    const days = Math.round((todayMs - raised) / DAY_MS);
    if (days > oldestOpenDays) oldestOpenDays = days;
  }

  // Awaiting sub-line: the longest any awaiting issue has been blocked, in
  // whole days from statusEnteredAt — the supplier-responsiveness signal.
  let oldestAwaitingDays = 0;
  for (const issue of issues) {
    if (issue.status !== "awaiting_response") continue;
    const entered = isoToUTCDay(issue.statusEnteredAt);
    if (entered === null) continue;
    const days = Math.round((todayMs - entered) / DAY_MS);
    if (days > oldestAwaitingDays) oldestAwaitingDays = days;
  }

  let overdueActions = 0;
  // Overdue-actions sub-line: actions completed LATE within the current
  // calendar month — a follow-through signal alongside the live overdue count.
  let doneLateThisMonth = 0;
  const todayMonth = todayIso.slice(0, 7); // YYYY-MM
  for (const issue of issues) {
    if (!issue.actions) continue;
    for (const action of Object.values(issue.actions)) {
      if (
        deriveIssueActionState(todayIso, action.dueDate, action.doneAt) ===
        "overdue"
      ) {
        overdueActions += 1;
      }
      if (
        action.doneAt &&
        action.doneAt > action.dueDate &&
        action.doneAt.slice(0, 7) === todayMonth
      ) {
        doneLateThisMonth += 1;
      }
    }
  }

  // Resolved sub-line: mean whole-days from raisedAt to resolvedAt over the
  // issues resolved in the trailing 30 days.
  const resolvedWindow: number[] = [];
  for (const i of issues) {
    if (!i.resolvedAt) continue;
    const resolved = isoToUTCDay(i.resolvedAt);
    if (resolved === null) continue;
    const ageDays = Math.round((todayMs - resolved) / DAY_MS);
    if (ageDays < 0 || ageDays > 30) continue;
    const raised = isoToUTCDay(i.raisedAt);
    if (raised !== null) {
      resolvedWindow.push(Math.round((resolved - raised) / DAY_MS));
    }
  }
  const resolvedLast30d = resolvedWindow.length;
  const avgResolveDays =
    resolvedWindow.length > 0
      ? Math.round(
          resolvedWindow.reduce((a, b) => a + b, 0) / resolvedWindow.length,
        )
      : 0;

  return (
    <div
      data-testid="issue-summary-strip"
      className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4"
    >
      <MetricCard
        testId="issue-metric-open"
        label="Open"
        value={unresolvedCount}
        valueTone={unresolvedCount > 0 ? "danger" : "neutral"}
        subtitle={
          unresolvedCount > 0
            ? `oldest ${oldestOpenDays}d open`
            : "no open issues"
        }
      />
      <MetricCard
        testId="issue-metric-awaiting"
        label="Awaiting response"
        value={awaitingCount}
        valueTone={awaitingCount > 0 ? "danger" : "neutral"}
        subtitle={
          awaitingCount > 0
            ? `oldest ${oldestAwaitingDays}d in this state`
            : "none awaiting a response"
        }
      />
      <MetricCard
        testId="issue-metric-overdue-actions"
        label="Overdue actions"
        value={overdueActions}
        valueTone={overdueActions > 0 ? "danger" : "neutral"}
        subtitle={`${doneLateThisMonth} done late this month`}
      />
      <MetricCard
        testId="issue-metric-resolved-30d"
        label="Resolved last 30d"
        value={resolvedLast30d}
        valueTone="neutral"
        subtitle={
          resolvedLast30d > 0
            ? `avg time to resolve ${avgResolveDays}d`
            : "none resolved in the last 30 days"
        }
      />
    </div>
  );
}
